import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  asyncStandupPath,
  dailyStandupCostPath,
  githubDigestPath,
  meetingWastePath,
  publicChangelogRepositoryPath,
  replaceStandupsPath,
  rootPath,
  signInPath,
  standupBotsPath,
} from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const CAMPAIGN = 'utm_source=digest-examples&utm_campaign=examples'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

const EXAMPLES = [
  {
    id: 'feature',
    kicker: 'Feature',
    repo: 'sunergos-ro/ebaza',
    owner: 'sunergos-ro',
    name: 'ebaza',
    when: '8 October 2026',
    summary:
      'Custom alerts now support push and email choices, with defaults and backfill for eligible accounts.',
    pills: 'Feature, then Improvement',
    slack: 'Feature',
    lines: '495 lines added, 98 removed',
    useful:
      'A manager can see the user-facing change: alerts can be push or email, and eligible accounts received defaults and a backfill. The sentence does not stop at "worked on alerts."',
    standup:
      'Worked on alerts.',
  },
  {
    id: 'bugfix',
    kicker: 'Bugfix',
    repo: 'sunergos-ro/invoice-generator',
    owner: 'sunergos-ro',
    name: 'invoice-generator',
    when: '12 March 2026',
    summary: 'Fixes broken footer link by updating the GitHub license URL to the correct file.',
    pills: 'Bug Fix',
    slack: 'Bugfix',
    lines: '1 line added, 1 removed',
    useful:
      'The line names the broken footer link and the correction. A manager knows which link, not only that a bug was fixed. The public pill says Bug Fix. Slack uses the tag name Bugfix.',
    standup:
      'Fixed a link.',
  },
  {
    id: 'refactoring',
    kicker: 'Refactoring',
    repo: 'sunergos-ro/squingo',
    owner: 'sunergos-ro',
    name: 'squingo',
    when: '16 January 2026',
    summary: 'Simplified leaderboard rank update to use a safer, clearer query for computing player ranks.',
    pills: 'Refactoring',
    slack: 'Refactoring',
    lines: '11 lines added, 9 removed',
    useful:
      'The line does not invent a new feature. It says the rank query was rewritten to be safer and clearer. A manager can tell that player ranks are the job, and the implementation changed.',
    standup:
      'Cleaned up the leaderboard.',
  },
] as const

function campaignPath(path: string) {
  return path === signInPath() ? `${path}?${CAMPAIGN}` : path
}

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className={linkClass}>
      {children}
    </Link>
  )
}

export function DigestExamplesArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">Digest examples</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          Three real summaries from public changelogs. Each one is annotated for an engineering manager: why the
          sentence is useful, where Slack files it, what the email shows, and the vague standup line it replaces.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 sm:p-8">
        <div className="text-muted-foreground space-y-3 text-sm leading-relaxed sm:text-base">
          <p>
            These are not a single day of one team. They are three separate changes, copied from public pages on 9
            October 2026. The author on each page is Raul Popadineti. The standup sentence under each one is an
            illustration. It is not a quote from a meeting.
          </p>
          <p>
            <TextLink href={rootPath()}>Git Digest</TextLink> writes the summary for someone who will not read the
            diff. Slack groups the change under its first tag: Feature, Bugfix, Improvement, Chore, or Refactoring.
            Email shows the summary, the tags, and lines added and removed, and it includes the first five changes in
            each repository.
          </p>
        </div>
      </Card>

      <section className="space-y-8">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Three real lines</h2>
        {EXAMPLES.map((example, index) => (
          <Card key={example.id} className="overflow-hidden">
            <CardContent className="space-y-4 pt-6">
              <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">
                Example {index + 1}. {example.kicker}
              </p>
              <p className="text-muted-foreground text-sm">
                <TextLink href={publicChangelogRepositoryPath(example.owner, example.name)}>{example.repo}</TextLink>
                <span>, {example.when}</span>
              </p>
              <blockquote className="border-golden-500 bg-golden-500/5 text-foreground rounded-xl border-l-4 px-4 py-3 text-base leading-relaxed">
                {example.summary}
              </blockquote>
              <dl className="grid gap-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-foreground font-medium">Public pills</dt>
                  <dd className="text-muted-foreground mt-1">{example.pills}</dd>
                </div>
                <div>
                  <dt className="text-foreground font-medium">Slack header</dt>
                  <dd className="text-muted-foreground mt-1">{example.slack}, because it is the first tag</dd>
                </div>
                <div>
                  <dt className="text-foreground font-medium">Email line counts</dt>
                  <dd className="text-muted-foreground mt-1">{example.lines}</dd>
                </div>
              </dl>
              <p className="text-muted-foreground text-sm leading-relaxed">
                <strong className="text-foreground">Why it is useful. </strong>
                {example.useful}
              </p>
              <div className="bg-muted/40 space-y-2 rounded-xl border px-4 py-3 text-sm leading-relaxed">
                <p className="text-foreground font-medium">Before, as an illustration only</p>
                <p className="text-muted-foreground">{example.standup}</p>
                <p className="text-foreground font-medium">After, the real summary</p>
                <p className="text-muted-foreground">{example.summary}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">How to read the format</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            The summarizer is told to keep an ordinary summary concise, under 140 characters, and to leave the
            category in the tags instead of prefixing the sentence with Feature or Bugfix. All three sentences above
            are shorter than that limit. A change that touches more than a dozen files can run longer.
          </p>
          <p>
            On Slack, a daily message is titled with that date. A weekly message is titled with the date range. Changes
            sit in a list under the first tag. If the change has a pull request, the line links that pull request.
            Otherwise it links a short commit id. The public changelog can show a second pill, such as Improvement
            after Feature on the ebaza line. Slack does not repeat the change under the second tag.
          </p>
          <p>
            Email uses the same summary and the same line counts the public page shows: 495 and 98, 1 and 1, and 11
            and 9. It stops at five changes per repository. A daily digest covers the previous 24 hours. A weekly
            digest covers the past week and is prepared on Mondays. Both go out for workspaces with an active
            subscription, to members who have email on, and to the Slack channel stored when you connect Slack.
          </p>
          <p>
            The homepage Unlimited plan is $29 per month, or $25 per month on yearly billing, for the team rather than
            per seat. A lifetime option is $1,000 once. The{' '}
            <TextLink href={campaignPath(replaceStandupsPath())}>replace standups</TextLink> page walks through setup.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">When the vague line is still fair</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A digest cannot see hiring, support, or design that never lands in the repositories you selected. For that
            work, a short standup or a form is the better update. The{' '}
            <TextLink href={campaignPath(standupBotsPath())}>standup bots comparison</TextLink> and the{' '}
            <TextLink href={campaignPath(asyncStandupPath())}>async standup playbook</TextLink> cover that case.
          </p>
          <p>
            If you are pricing the meeting these lines replace, use the{' '}
            <TextLink href={campaignPath(dailyStandupCostPath())}>daily standup cost</TextLink> page and the{' '}
            <TextLink href={campaignPath(meetingWastePath())}>wasted money meeting calculator</TextLink>. Git Digest is not
            Gitingest. The <TextLink href={campaignPath(githubDigestPath())}>GitHub digest comparison</TextLink>{' '}
            separates a team digest from a repo turned into text for a model.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Digest examples FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Where the three lines come from, and how Slack, email, and a standup line differ.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Get this shape of update from your repos</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub and send daily or weekly commit summaries to Slack and email. The lines above are the public
          form of that summary.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={campaignPath(signInPath())}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
